import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { reportsService } from "../../../../api";
import { Table, TableSkeleton, Button, BackArrow } from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { usePageTitle } from "../../../../context/navigationContext";
import { useQueryParam, NumberParam, StringParam } from "use-query-params";

interface RevenueReport {
  id: number;
  start_date: string;
  end_date: string;
  report_month_year: string;
  total_revenue: string;
  currency_code: string;
  generating: boolean;
  processed: number;
  total_revenue_per_location: Array<{
    name: string;
    revenue: string;
  }>;
}

const RevenueReportsList: React.FC = () => {
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  const [t] = useTranslation();
  usePageTitle(t("REVENUE_REPORTS_TITLE"));
  const [page, setPage] = useQueryParam("page", NumberParam);
  const [ordering, setOrdering] = useQueryParam("ordering", StringParam);
  const [pageSize, setPageSize] = useQueryParam("page_size", NumberParam);
  const [reports, setReports] = useState<RevenueReport[]>([]);
  const [paginationData, setPaginationData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<any>({});

  useEffect(() => {
    loadReports();
    loadPermissions();
  }, [page, ordering, pageSize]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await reportsService.getRevenueReportsList({
        page: page || 1,
        ordering: ordering || "-start_date",
        pageSize: pageSize || 10,
      });
      setReports(response.data.objects);
      setPaginationData(response.data);
    } catch (error: any) {
      viewAlert({
        severity: "error",
        message: error?.response?.data?.detail || t("FAILED_LOAD_REPORTS"),
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await reportsService.getRevenueReportPermissions();
      setPermissions(response.data);
    } catch (error) {
      // Permissions are optional
    }
  };

  const formatCurrency = (value: string, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(parseFloat(value));
  };

  const columns = [
    {
      title: t("REPORT_FOR"),
      key: "report_month_year",
    },
    {
      title: t("TOTAL_REVENUE"),
      key: "total_revenue_formatted",
    },
    {
      title: t("FINISHED_GENERATION"),
      key: "generating_status",
    },
    {
      title: t("CLIENTS_PROCESSED"),
      key: "processed_formatted",
    },
  ];

  const rowData = reports.map((report) => ({
    id: report.id,
    report_month_year: report.report_month_year,
    total_revenue_formatted: formatCurrency(report.total_revenue, report.currency_code),
    generating_status: report.generating ? "⊘" : "✓",
    processed_formatted: `${report.processed}%`,
    navigate: `/utilities/reports/revenue-reports/${report.id}`,
  }));

  return (
    <Container>
      <BackArrow fallbackPath="/utilities/reports" />
      <Header>
        <Title>{t("REVENUE_REPORTS_TITLE")}</Title>
        {permissions["revenue_reports.trigger_revenue_report_generation"] && (
          <Button
            variant="primary"
            size="display"
            title={`+ ${t("GENERATE_REPORT")}`}
            onClick={() => navigate("/utilities/reports/revenue-reports/generate")}
          />
        )}
      </Header>

      {loading ? (
        <TableSkeleton />
      ) : (
        <Table
          columns={columns}
          rows={rowData}
          paginationData={paginationData}
          updateData={loadReports}
          loading={loading}
          hideCheckbox={true}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  margin-top: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette[900]};
`;

export default RevenueReportsList;
